import * as React from 'react'

import { useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { Provider } from 'modules/ats/lib/enums'
import JobBoardCard, {
  ProviderComponentProps,
} from 'modules/ats/pages/JobPostingPromotion/JobBoards/JobBoardCard'
import type Job from 'modules/ats/store/models/Job'
import type { ScheduleType } from 'modules/ats/store/models/Job'
import { companyMainLocation } from 'modules/locations/store/collections/locations'

import { Action } from './Action'
import { LinkedInCampaignForm, LinkedInContractForm, VonqCampaignForm } from './Forms'
import { LinkedInCampaign } from './LinkedInCampaign'
import { LinkedInContract } from './LinkedInContract'
import { MissingFieldsModal } from './MissingFieldsModal'
import { StepEvent, StepParams } from './StepParams'
import type { VonqData } from './Types'
import { VonqCampaign } from './VonqCampaign'
import LinkedInLogo from './linkedin-logo.svg'

const linkedInCampaignFormInitialData = (job: Job) => {
  const location = job.location || companyMainLocation()
  const scheduleType: ScheduleType = job.attributes.schedule_type || 'full_time'
  return {
    countryCode: location.attributes.country,
    postalCode: location.attributes.postal_code || '-',
    employmentStatus: scheduleType.toUpperCase(),
    showPosterInfo: 'false',
  }
}

export const Vonq = observer(({ promotions, job }: ProviderComponentProps) => {
  const linkedInContractForm = React.useRef(new LinkedInContractForm({})).current
  const vonqCampaignForm = React.useRef(new VonqCampaignForm({})).current
  const linkedInCampaignForm = React.useRef(
    new LinkedInCampaignForm(linkedInCampaignFormInitialData(job))
  ).current
  const [showPromote, toggleShowPromote] = useToggle(false)
  const promotion = promotions.filter({ provider: Provider.Vonq })[0]
  const promotionStatus = promotion?.attributes?.status
  const promotionData = promotion?.attributes?.data as VonqData | undefined
  const [hasContract, setHasContract] = React.useState<boolean>(!!promotionData?.contract)
  const [currentStep, setCurrentStep] = React.useState<number>(1)
  const [missingFields, setMissingFields] = React.useState<Array<string>>([])
  const [showMissingFieldsModal, setShowMissingFieldsModal] = React.useState<boolean>(false)
  const totalSteps = hasContract ? 2 : 3

  useAsyncData(async () => {
    const response = await promotions.missing_fields(stepParams.job.id, Provider.Vonq)
    setMissingFields(response.missing_fields)
  }, [])

  const { performAction: onStopPromoting } = useAsyncAction(
    async () => {
      await promotion?.cancel()
    },
    {
      successMessage: i18n.t('ats.job_details.promotion.vonq.promotion_stopped'),
    }
  )

  const openPromoteModal = () => {
    toggleShowPromote(missingFields.length === 0)()
    setShowMissingFieldsModal(missingFields.length > 0)
  }

  const stopWizard = () => {
    toggleShowPromote(false)()
    handleStepEvent(StepEvent.Restart)
    setHasContract(!!promotionData?.contract)
  }

  const handleStepEvent = (event: StepEvent) => {
    switch (event) {
      case StepEvent.Restart:
        setCurrentStep(1)
        break
      case StepEvent.Next:
        setCurrentStep(currentStep + 1)
        break
      case StepEvent.Stop:
        stopWizard()
        break
      case StepEvent.Back:
        setCurrentStep(currentStep - 1)
        break
    }
  }

  const stepParams: StepParams = {
    job,
    promotions,
    onStepEvent: handleStepEvent,
    onClose: stopWizard,
    progress: { amount: currentStep, total: totalSteps },
    linkedInContractForm,
    vonqCampaignForm,
    linkedInCampaignForm,
  }

  const campaignStep = hasContract ? currentStep : currentStep - 1
  const showLinkedInContract = showPromote && !hasContract && currentStep === 1
  const showVonqCampaign = showPromote && campaignStep === 1
  const showLinkedInCampaign = showPromote && campaignStep === 2

  return (
    <>
      <JobBoardCard
        key="vonq-linkedin"
        title={i18n.t('ats.job_details.promotion.vonq.linkedin.title')}
        description={i18n.t('ats.job_details.promotion.vonq.linkedin.description')}
        websiteUrl="https://www.linkedin.com/"
        logo={LinkedInLogo}
        action={
          <Action
            onPromote={openPromoteModal}
            onStopPromoting={onStopPromoting}
            status={promotionStatus}
          />
        }
        status={promotionStatus}
      />
      {showMissingFieldsModal && (
        <MissingFieldsModal
          fields={missingFields}
          onClose={() => setShowMissingFieldsModal(false)}
        />
      )}

      {showLinkedInContract && <LinkedInContract {...stepParams} />}
      {showVonqCampaign && <VonqCampaign {...stepParams} />}
      {showLinkedInCampaign && <LinkedInCampaign {...stepParams} />}
    </>
  )
})
